import { DrawerComponent } from 'component.drawer';
import { updateCart, changeCart, addToCart, getCart } from 'util.helpers';
import { EVENTS, subscribe, publish } from 'util.pubsub';

class Minicart extends DrawerComponent {
  connectedCallback() {
    super.connectedCallback?.();
    this.checkGiftProduct();
    document.addEventListener('fg-cart:auto-updated', this.handleCartAutoUpdated.bind(this));
  }


  async handleCartAutoUpdated(event) {
    this.preload();
    await this.render();
  }

  get giftData() {
    const script = this.querySelector('[data-cart-gift-data]');
    if (!script) return {};
    try {
      return JSON.parse(script.textContent);
    } catch {
      return {};
    }
  }

  get shouldAddGift() {
    const { giftProductId, giftThreshold, cartTotal, hasGift, enableGiftProduct } = this.giftData;
    return enableGiftProduct && giftProductId && giftThreshold && cartTotal >= giftThreshold && !hasGift;
  }

  get shouldRemoveGift() {
    const { giftProductId, giftThreshold, cartTotal, hasGift } = this.giftData;
    return giftProductId && giftThreshold && cartTotal < giftThreshold && hasGift;
  }

  get preloading() {
    return this.hasAttribute('data-preloading');
  }

  set preloading(value) {
    this.toggleAttribute('data-preloading', value);
  }

  preload() {
    if (this.preloading) return;
    
    this.preloading = true;
    this.classList.add('loading');
    
    setTimeout(() => {
      if (this.preloading) {
        this.preloadDone();
      }
    }, 5000);
  }

  preloadDone() {
    this.preloading = false;
    this.classList.remove('loading');
  }

  checkGiftProduct() {
    console.log('checkGiftProduct', this.giftData);
    if (!this.giftData) return;
    if (this.shouldAddGift) {
      console.log('shouldAddGift', this.shouldAddGift);
      this.addGiftProduct();
    } else if (this.shouldRemoveGift) {
      console.log('shouldRemoveGift', this.shouldRemoveGift);
      this.removeGiftProduct();
    }
  }

  async addGiftProduct() {
    try {
      const formData = new FormData();
      formData.append('id', this.giftData.giftProductId);
      formData.append('quantity', '1');
      formData.append('properties[_gift]', 'true');
      formData.append('sections', 'mini-cart');
      
      const cartData = await addToCart(formData);
      this.render(cartData);
    } catch (error) {
      console.error('Error adding gift product:', error);
    }
  }

  async removeGiftProduct() {
    const giftItems = this.querySelectorAll('mini-cart-item');
    for (const item of giftItems) {
      if (item.dataset.gift === 'true') {
        console.log('---- removing gift', item.dataset.key);
        await this.updateQty(item.dataset.key, 0);
        break;
      }
    }
  }

  async updateQty(key, qty, _ts = null) {
    if (_ts) {
      await this.updateQtyBulk(_ts, qty);
      return;
    }

    let cartData = null;
    
    const updates = { [key]: qty };
    const sections = 'mini-cart';
    this.preload();
    publish(EVENTS.cartBeforeChange, { updates, sections });
    try {
      cartData = await updateCart({ 
        data: { updates, sections }
      });
      this.render(cartData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      this.preloadDone();
      publish(EVENTS.cartChange, { updates, cartData });
    }
  }

  async updateQtyBulk(ts, qty) {
    const itemsToRemove = Array.from(this.querySelectorAll('mini-cart-item'))
      .filter(item => item.dataset.ts === ts);
    const keys = itemsToRemove.map(item => item.dataset.key);
    const updates = Object.fromEntries(keys.map(key => [key, qty]));

    this.preload();
    publish(EVENTS.cartBeforeChange, { updates, sections: 'mini-cart' });
    let cartData = null;
    try {
      cartData = await updateCart({ 
        data: {
          updates,
          sections: 'mini-cart'
        }
      });
      this.render(cartData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      this.preloadDone();
      publish(EVENTS.cartChange, { updates, cartData });
    }
  }

  async changePlan({ key, planId, qty }) {
    this.preload();
    publish(EVENTS.cartBeforeChange, { updates: {}, sections: 'mini-cart' });
    let cartData = null;
    try {
      cartData = await changeCart({ 
        id: key, 
        quantity: qty, 
        planId, 
        sections: 'mini-cart' 
      });
      this.render(cartData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      this.preloadDone();
      publish(EVENTS.cartChange, { updates: {}, cartData });
    }
  }

  async render(cartData) {
    let cartJson = cartData;
    if (!cartJson) {
      cartJson = await getCart();
    }
    const html = cartJson.sections['mini-cart'];
    const newWindowContent = new DOMParser().parseFromString(html, 'text/html').querySelector('.drawer__window');
    const oldWindowContent = this.querySelector('.drawer__window');
    
    if (!newWindowContent || !oldWindowContent) {
      console.error("Could not find drawer window content to update.");
      return;
    }


    oldWindowContent.innerHTML = newWindowContent.innerHTML;
    
    this.preloadDone();
    const cartCount = newWindowContent.dataset.itemCount || '0';
    console.log('cartCount', cartCount);
    publish(EVENTS.cartRendered, { detail: { cartJson, cartCount } });
    this.checkGiftProduct();
  }

  open(cartData) {
    if (cartData) {
      this.render(cartData);
    }
    super.open();
  }
}

class MinicartItem extends HTMLElement {
  connectedCallback() {
    this.minicart = document.querySelector('mini-cart');
    this.key = this.dataset.key;
    this.ts = this.dataset.ts || null;
    this.removeButton = this.querySelector('.js-cart-item-remove');
    // Listen for qty-picker changes
    this.qtyPicker = this.querySelector('qty-picker');
    if (this.qtyPicker) {
      this.unsubscribeQtyChange = subscribe(EVENTS.qtyChange, this.handleQtyChange.bind(this));
    }
    
    this.removeButton?.addEventListener('click', this.handleRemove.bind(this));
    this.querySelector('[data-change-plan]')?.addEventListener('change', this.handlePlanChange.bind(this));
  }

  get preloading() {
    return this.hasAttribute('data-item-preloading');
  }

  set preloading(value) {
    this.toggleAttribute('data-item-preloading', value);
  }

  disconnectedCallback() {
    this.unsubscribeQtyChange?.();
  }

  async handleQtyChange(event) {
    const { value, id } = event.detail;
    if (id === this.key) {
      this.preloading = true;
      await this.minicart?.updateQty(this.key, value, this.ts);
      this.preloading = false;
    }
  }

  async handleRemove() {
    this.preloading = true;
    await this.minicart?.updateQty(this.key, 0, this.ts);
    this.preloading = false;
  }

  handlePlanChange(event) {
    const planId = event.target.value;
    const qty = this.dataset.qty;
    this.minicart?.changePlan({ key: this.key, planId, qty });
  }
}

class CartBubble extends HTMLElement {
  connectedCallback() {
    this.unsubscribeCartRendered = subscribe(EVENTS.cartRendered, this.handleCartRendered.bind(this));
  }

  disconnectedCallback() {
    this.unsubscribeCartRendered?.();
  }

  handleCartRendered(event) {
    console.log('handleCartRendered', event.detail);
    const { cartCount } = event.detail;
    if (cartCount !== undefined) {
      this.textContent = cartCount;
    }
  }
}

class CartNote extends HTMLElement {
  connectedCallback() {
    this.accordion = this.querySelector('accordion-component');
    this.cart = this.closest('mini-cart');
    this.querySelector('.js-note-update')?.addEventListener('click', this.handleNoteUpdate.bind(this));
  }
  async handleNoteUpdate(event) {
    event.preventDefault();
    
    const noteTextarea = this.querySelector('#cart-note');
    if (!noteTextarea) return;
    
    const note = noteTextarea.value;
    
    this.cart.preload();
    
    try {
      const cartData = await updateCart({
        data: {
          note,
          sections: 'mini-cart'
        }
      });
      this.cart.render(cartData);
    } catch (error) {
      console.error('Error updating cart note:', error);
    } finally {
      this.cart.preloadDone();
    }
  }
}

class UpsellBlock extends HTMLElement {
  connectedCallback() {
    this.upsellProducts = this.dataset.upsellProducts;
    this.upsellTitle = this.dataset.upsellTitle;
    this.cart = this.closest('mini-cart');
    this.limit = this.dataset.limit * 1;
    this.intent = this.dataset.intent;
    this.loadUpsell();
  }
  async loadUpsell() {
    const upsellProducts = [];

    const cartItem = this.cart.querySelector('mini-cart-item');
    if (cartItem) {
      // Fetch upsells for cart items
      const id = cartItem.dataset.productId;
      await fetch(`/recommendations/products?section_id=cart-recommendations&product_id=${id}&limit=${this.limit}&intent=${this.intent}`)
      .then(response => response.text())
      .then(text => {
        const items = new DOMParser().parseFromString(text, 'text/html').querySelectorAll('.block-upsell__item');
        Array.from(items).slice(0, this.limit).forEach(item => upsellProducts.push(item));
      })
    }

    // Insert recommendations to cart slider
    if (upsellProducts.length >= this.limit) {
      this.querySelector('.js-upsell-items').innerHTML = '';
      upsellProducts.forEach((product, index) => {
        const item = document.createElement('div');
        item.innerHTML = product.outerHTML;
        this.querySelector('.js-upsell-items').prepend(item);
      })
    }

  }
}

customElements.define('mini-cart', Minicart);
customElements.define('mini-cart-item', MinicartItem);
customElements.define('cart-bubble', CartBubble);
customElements.define('cart-note', CartNote);
customElements.define('block-upsell', UpsellBlock);

// No need for default export if not importing elsewhere
